import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import dayjs from 'dayjs';

/**
 * FiveDayCalendar - A compact 5-day horizontal date picker
 * Used across the booking system for consistent date selection UX
 *
 * Props:
 * - selectedDate: string (YYYY-MM-DD) - Currently selected date
 * - onDateSelect: function(dateString) - Called when a date is selected
 * - startDate: string (YYYY-MM-DD) - The first date to show in the 5-day view
 * - onNavigate: function(days) - Called to navigate forward/backward (e.g., onNavigate(5) or onNavigate(-5))
 * - showTodayButton: boolean - Whether to show "Today" button (default: true)
 * - compact: boolean - Use more compact styling for modals (default: false)
 */
function FiveDayCalendar({
  selectedDate,
  onDateSelect,
  startDate,
  onNavigate,
  showTodayButton = true,
  compact = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Generate 5 days starting from startDate
  const days = [];
  for (let i = 0; i < 5; i++) {
    const date = dayjs(startDate).add(i, 'day');
    days.push({
      date: date.format('YYYY-MM-DD'),
      dayName: date.format('ddd'),
      dayNumber: date.format('D'),
      monthName: date.format('MMM'),
      isToday: date.isSame(dayjs(), 'day'),
      isPast: date.isBefore(dayjs(), 'day'),
    });
  }

  const canGoBack = dayjs(startDate).isAfter(dayjs(), 'day');
  const isShowingToday = dayjs(startDate).isSame(dayjs(), 'day') ||
                          dayjs(startDate).isBefore(dayjs(), 'day');

  const handleGoToToday = () => {
    const today = dayjs().format('YYYY-MM-DD');
    // Calculate how many days to navigate
    const currentStart = dayjs(startDate);
    const todayDate = dayjs();
    const diff = todayDate.diff(currentStart, 'day');
    onNavigate(diff);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Navigation Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: compact ? 1.5 : 2,
        px: compact ? 0 : 1,
      }}>
        <IconButton
          onClick={() => onNavigate(-5)}
          disabled={!canGoBack}
          size={compact ? 'small' : 'medium'}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'grey.100' },
            '&.Mui-disabled': { opacity: 0.3 },
          }}
        >
          <ChevronLeftIcon fontSize={compact ? 'small' : 'medium'} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant={compact ? 'body2' : 'subtitle1'}
            fontWeight={600}
            sx={{ textAlign: 'center' }}
          >
            {dayjs(startDate).format('MMMM YYYY')}
            {dayjs(startDate).month() !== dayjs(startDate).add(4, 'day').month() && (
              <span> - {dayjs(startDate).add(4, 'day').format('MMMM YYYY')}</span>
            )}
          </Typography>

          {showTodayButton && !isShowingToday && (
            <Button
              size="small"
              variant="text"
              startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
              onClick={handleGoToToday}
              sx={{
                fontSize: '0.75rem',
                color: 'primary.main',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
              }}
            >
              Today
            </Button>
          )}
        </Box>

        <IconButton
          onClick={() => onNavigate(5)}
          size={compact ? 'small' : 'medium'}
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <ChevronRightIcon fontSize={compact ? 'small' : 'medium'} />
        </IconButton>
      </Box>

      {/* 5 Day Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: compact ? 0.5 : { xs: 0.5, sm: 1.5 },
      }}>
        {days.map((day) => {
          const isSelected = selectedDate === day.date;
          const isDisabled = day.isPast;

          return (
            <Box
              key={day.date}
              onClick={() => !isDisabled && onDateSelect(day.date)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: compact ? 1 : { xs: 1.5, sm: 2 },
                px: compact ? 0.25 : { xs: 0.5, sm: 1 },
                borderRadius: compact ? 1.5 : 2,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                bgcolor: isSelected
                  ? 'primary.main'
                  : day.isToday
                    ? 'rgba(139, 38, 53, 0.08)'
                    : 'background.paper',
                border: '2px solid',
                borderColor: isSelected
                  ? 'primary.main'
                  : day.isToday
                    ? 'primary.light'
                    : 'divider',
                opacity: isDisabled ? 0.4 : 1,
                '&:hover': !isDisabled && !isSelected ? {
                  bgcolor: 'rgba(139, 38, 53, 0.08)',
                  borderColor: 'primary.light',
                  transform: compact ? 'none' : 'translateY(-2px)',
                  boxShadow: compact ? 'none' : '0 4px 12px rgba(139, 38, 53, 0.15)',
                } : {},
              }}
            >
              {/* Day Name */}
              <Typography
                sx={{
                  fontSize: compact ? '0.6rem' : { xs: '0.65rem', sm: '0.75rem' },
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: isSelected ? 'white' : 'text.secondary',
                  mb: 0.5,
                }}
              >
                {day.dayName}
              </Typography>

              {/* Day Number */}
              <Typography
                sx={{
                  fontSize: compact ? '1rem' : { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 700,
                  lineHeight: 1,
                  color: isSelected ? 'white' : 'text.primary',
                }}
              >
                {day.dayNumber}
              </Typography>

              {/* Month */}
              <Typography
                sx={{
                  fontSize: compact ? '0.55rem' : { xs: '0.6rem', sm: '0.7rem' },
                  color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                  mt: 0.5,
                }}
              >
                {day.monthName}
              </Typography>

              {/* Today indicator */}
              {day.isToday && !isSelected && (
                <Box
                  sx={{
                    width: compact ? 3 : 4,
                    height: compact ? 3 : 4,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    mt: 0.5,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default FiveDayCalendar;
